import { Box, Tab, Tabs, CircularProgress, Alert, Avatar, MenuItem, Typography, Select, IconButton } from "@mui/material";
import CustomLabel from "../../components/CustomLabel";
import PageHeader from "../../components/PageHeader";
import React from "react";
import { COLORS } from "../../assets/theme";
import CustomTextField from "../../components/CustomTextField";
import { getClientById, updateClient } from "../../services/ClientsService";
import { useParams } from "react-router-dom";
import { useTabParam } from "../../hooks/useTabParam";
import CustomButton from "../../components/CustomButton";
import { getAppointmentsForClient } from "../../services/AppointmentService";
import { toast } from "react-toastify";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import CustomDateField from "../../components/CustomDateField";
import { getAllInvoicesForUser } from "../../services/InvoicesService";
import { countries } from "../../assets/utils/countries";
import CustomSelect from "../../components/CustomSelect";

 const DEFAULT_IMAGE =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
      <circle cx="100" cy="100" r="100" fill="#c4c4c4"/>
      <circle cx="100" cy="80" r="38" fill="#f2f2f2"/>
      <path d="M40 170c0-33 27-60 60-60s60 27 60 60" fill="#f2f2f2"/>
    </svg>`
  );
 
const ClientDetails: React.FC =  () => {
    
    const { clientId } = useParams<{ clientId: string }>();
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    const [name, setName] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [phone, setPhone] = React.useState("");
    const [gender, setGender] = React.useState("");
    const [dob, setDob] = React.useState("");
    const [dobDay, setDobDay] = React.useState("");
    const [dobMonth, setDobMonth] = React.useState("");
    const [dobYear, setDobYear] = React.useState("");
    const [country, setCountry] = React.useState("");
    const [status, setStatus] = React.useState("");
    const [banner, setBanner] = React.useState<string | null>(null);
    const [isEditing, setIsEditing] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [initialData, setInitialData] = React.useState({
      name: "",
      email: "",
      phone: "",
      gender: "",
      dob: "",
      country: "",
      status: "",
      banner: null as string | null,
    });
    const [bannerFile, setBannerFile] = React.useState<File | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);
    const previewUrlRef = React.useRef<string | null>(null);

    const { tab, setTab, panelRef: tabPanelRef } = useTabParam([
      "personal",
      "bookings",
      "invoices",
    ]);
    const [appointments, setAppointments] = React.useState<any[]>([]);
    const [appointmentsLoading, setAppointmentsLoading] = React.useState(false);
    const [appointmentsError, setAppointmentsError] = React.useState<string | null>(null);
    const [invoices, setInvoices] = React.useState<any[]>([]);
    const [invoicesLoading, setInvoicesLoading] = React.useState(false);
    const [invoicesError, setInvoicesError] = React.useState<string | null>(null);
    const [invoicePage, setInvoicePage] = React.useState(1);
    
    const [page, setPage] = React.useState(1);
    const [size, setSize] = React.useState(10);
    const [total, setTotal] = React.useState(0);
    
    const formatDateInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeCountryValue = (value: any) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  return raw.startsWith("+") ? raw : `+${raw}`;
};

const [from, setFrom] = React.useState<string | null>(() => {
  const today = new Date();
  const weekAgo = new Date();
  weekAgo.setDate(today.getDate() - 7);
  return formatDateInput(weekAgo);
});

const [to, setTo] = React.useState<string | null>(() => {
  return formatDateInput(new Date());
});

    const invoiceTotals = React.useMemo(() => {
      const totals = (Array.isArray(invoices) ? invoices : []).reduce(
        (acc, inv) => {
          const total = Number(inv?.total ?? inv?.total_amount ?? inv?.net_amount) || 0;
          const balance = Number(inv?.balance ?? inv?.remaining ?? 0) || 0;
          const paid = total - balance;
          acc.total += total;
          acc.paid += paid;
          acc.remaining += balance;
          return acc;
        },
        { total: 0, paid: 0, remaining: 0 }
      );

      return {
        total: totals.total.toFixed(2),
        paid: totals.paid.toFixed(2),
        remaining: totals.remaining.toFixed(2),
      };
    }, [invoices]);

    const invoicePageSize = 10;
    const invoiceTotal = Array.isArray(invoices) ? invoices.length : 0;
    const invoiceTotalPages = Math.max(1, Math.ceil(invoiceTotal / invoicePageSize));
    const pagedInvoices = React.useMemo(() => {
      const safePage = Math.min(Math.max(1, invoicePage), invoiceTotalPages);
      const startIndex = (safePage - 1) * invoicePageSize;
      return (Array.isArray(invoices) ? invoices : []).slice(
        startIndex,
        startIndex + invoicePageSize
      );
    }, [invoices, invoicePage, invoiceTotalPages]);

    React.useEffect(() => {
      if (invoicePage > invoiceTotalPages) {
        setInvoicePage(invoiceTotalPages);
      }
    }, [invoicePage, invoiceTotalPages]);
    const days = React.useMemo(() => Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")), []);
    const months = React.useMemo(() => Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")), []);
    const years = React.useMemo(() => {
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 120 }, (_, i) => String(currentYear - i));
    }, []);

    const syncDobParts = React.useCallback((dateStr: string) => {
      if (dateStr) {
        const parsed = new Date(dateStr);
        if (!Number.isNaN(parsed.getTime())) {
          setDobDay(String(parsed.getDate()).padStart(2, "0"));
          setDobMonth(String(parsed.getMonth() + 1).padStart(2, "0"));
          setDobYear(String(parsed.getFullYear()));
          return;
        }
      }
      setDobDay("");
      setDobMonth("");
      setDobYear("");
    }, []);

    React.useEffect(() => {
      if (bannerFile) {
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
        }
        const preview = URL.createObjectURL(bannerFile);
        previewUrlRef.current = preview;
        setBanner(preview);
      }
      return () => {
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
          previewUrlRef.current = null;
        }
      };
    }, [bannerFile]);

    React.useEffect(() => {
        const fetchClientDetails = async () => {
          try {
            setLoading(true);
            setError(null);
            if (clientId) {
              const clientData = await getClientById(clientId);
              const dobValue =
                clientData.DOB ||
                clientData.dob ||
                clientData.date_of_birth ||
                clientData.birthdate ||
                clientData.birth_date ||
                "";
              setName(clientData.name || clientData.full_name || "");
              setEmail(clientData.user.email || "");
              setPhone(clientData.phone || clientData.phoneNumber || "");
              setGender(clientData.gender || "");
              setDob(dobValue);
              syncDobParts(dobValue);
              const rawCountry =
                clientData.country ||
                clientData.country_code ||
                clientData.countryCode ||
                "";
              setCountry(normalizeCountryValue(rawCountry));
              const normalizedStatus = clientData.active === true ? 'Verified' : 'Unverified';
              setStatus(normalizedStatus);
              setBanner(clientData.banner || null);
              setInitialData({
                name: clientData.name || clientData.full_name || "",
                email: clientData.user.email || "",
                phone: clientData.phone || clientData.phoneNumber || "",
                gender: clientData.gender || "",
                dob: dobValue,
                country: normalizeCountryValue(rawCountry),
                status: clientData.active === true ? "Verified" : "Unverified",
                banner: clientData.banner || null,
              });
                //load appts after client fetching
            
            }
          
          } catch (error) {
            console.error("Error fetching client details:", error);
            setError("Failed to load client details. Please try again.");
          } finally {
            setLoading(false);
          }
        };

        fetchClientDetails();
      }, [clientId, syncDobParts]);

      

      const loadAppointments = React.useCallback(
  async (id: string) => {
    try {
      setAppointmentsLoading(true);
      setAppointmentsError(null);

      const resp: any = await getAppointmentsForClient(id, {
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        // API pagination is zero-based while UI page is one-based.
        ...(page ? { page: Math.max(0, page - 1) } : {}),
        ...(size ? { size } : {}),
      });

      const payload = resp?.data ?? resp ?? {};
      const rows = Array.isArray(payload)
        ? payload
        : payload?.appointments ||
          payload?.items ||
          payload?.content ||
          payload?.data?.appointments ||
          payload?.data?.items ||
          payload?.data?.content ||
          [];

      const rawTotal =
        payload?.total ??
        payload?.totalElements ??
        payload?.count ??
        payload?.pagination?.total ??
        payload?.meta?.total ??
        payload?.data?.total ??
        payload?.data?.totalElements;

      const resolvedTotal = Number.isFinite(Number(rawTotal))
        ? Number(rawTotal)
        : Array.isArray(rows)
        ? rows.length
        : 0;

      setAppointments(Array.isArray(rows) ? rows : []);
      setTotal(resolvedTotal);
    } catch (error) {
      setAppointmentsError("Failed to load appointments. Please try again.");
    } finally {
      setAppointmentsLoading(false);
    }
  },
  [from, to, page, size]
);

React.useEffect(() => {
        if(!clientId) return;
        loadAppointments(clientId);
      }, [clientId, loadAppointments]);

      const loadInvoices = React.useCallback(
        async (id: string) => {
          try {
            setInvoicesLoading(true);
            setInvoicesError(null);
            const response: any = await getAllInvoicesForUser(String(id));
            let invoicesData: any[] = [];
            if (response && response.invoices) {
              invoicesData = response.invoices;
            } else if (response && response.data) {
              invoicesData = response.data;
            } else if (Array.isArray(response)) {
              invoicesData = response;
            }
            setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
          } catch (error) {
            setInvoices([]);
            setInvoicesError("Failed to load invoices. Please try again.");
          } finally {
            setInvoicesLoading(false);
          }
        },
        []
      );

React.useEffect(() => {
        if(tab !== 2 || !clientId) return;
  setInvoicePage(1);
        loadInvoices(clientId);
      }, [tab, clientId, loadInvoices]);

    const handleTabChange = async (_: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
      };

    const updateDobFromParts = (day: string, month: string, year: string) => {
      setDobDay(day);
      setDobMonth(month);
      setDobYear(year);

      if (day && month && year) {
        const formatted = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        setDob(formatted);
      } else {
        setDob("");
      }
    };

    const handleSave = async () => {
      if (!clientId) return;

      const trimmedName = name.trim();
      const trimmedCountry = country.trim();
      const trimmedPhone = phone.trim();

      if (!trimmedName || !trimmedCountry || !trimmedPhone) {
        toast.error("Name, Country, and Phone are required.");
        return;
      }

      const normalizeDob = (value: string) => {
        if (!value) return undefined;
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
      };

      try {
        setSaving(true);
        const payload: any = {};
        let formData: FormData | null = null;

        const assign = (key: string, value: any) => {
          if (formData) {
            if (value !== undefined && value !== null) formData.append(key, value as any);
          } else {
            payload[key] = value;
          }
        };

        if (bannerFile) {
          formData = new FormData();
          formData.append("banner", bannerFile);
        }

        if (trimmedName !== initialData.name) assign("full_name", trimmedName);
        if (email !== initialData.email) assign("email", email);
        if (trimmedPhone !== initialData.phone) assign("phone", trimmedPhone);
        if (trimmedCountry !== initialData.country) assign("country", trimmedCountry);
        if (gender !== initialData.gender) assign("gender", gender ? gender.toUpperCase() : gender);

        const dobToSend = dob || initialData.dob;
        const normalizedDob = normalizeDob(dobToSend);
        if (normalizedDob) assign("DOB", normalizedDob);

        if (status !== initialData.status)
          assign("active", status?.toLowerCase() === "verified");

        const hasChanges =
          (formData && formData.has("banner")) ||
          Object.keys(payload).length > 0;

        if (!hasChanges) {
          toast.info("No changes to save");
          setIsEditing(false);
          return;
        }

        await updateClient(clientId, formData || payload);
        toast.success("Client updated successfully");
        setInitialData({
          name: trimmedName,
          email,
          phone: trimmedPhone,
          gender,
          dob,
          country: trimmedCountry,
          status,
          banner,
        });
        setBannerFile(null);
        setIsEditing(false);
      } catch (err: any) {
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error_description ||
          err?.message ||
          "Failed to update client.";
        toast.error(message);
      } finally {
        setSaving(false);
      }
    };

    const sidePanel = (
      <Box
        flex={1}
        borderLeft={1}
        borderColor="divider"
        pl={3}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="center"
      >
        <Avatar
          src={typeof banner === "string" && banner.trim() ? banner : DEFAULT_IMAGE}
          variant="circular"
          imgProps={{
            onError: (event) => {
              const target = event.currentTarget as HTMLImageElement;
              if (target.src !== `${window.location.origin}${DEFAULT_IMAGE}`) {
                target.src = DEFAULT_IMAGE;
              }
            },
          }}
          alt="Client Banner"
          sx={{
            width: 220,
            height: 220,
            borderRadius: "50%",
            objectFit: "cover",
            cursor: isEditing ? "pointer" : "default",
          }}
          onClick={() => {
            if (isEditing) {
              fileInputRef.current?.click();
            }
          }}
        />
        <input
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setBannerFile(file);
            }
          }}
        />
    <Box
      justifyContent="flex-start"
      mt={5}
      alignSelf="stretch"
      width="100%"
          display="flex"
          flexDirection="column"
        >
            <CustomLabel text="Full Name" variant="subtitle1" gutterBottom={false} />
            <CustomLabel text={name || "-"} variant="body1" />

            <CustomLabel mt={2} 
            text="Mobile Number" variant="subtitle1" gutterBottom={false} />
            <CustomLabel
              text={`${country ? (country.startsWith("+") ? country : `+${country}`) : ""} ${phone}`.trim()}
              variant="body1"
            />

            <CustomLabel mt={2} text="Gender " variant="subtitle1" gutterBottom={false} />
            <CustomLabel text={gender} variant="body1" gutterBottom />
             </Box>
       { /*<Box mt={6}>
          <CustomButton
            label="Delete Client"
            variant="contained"
            bgColor={COLORS.RED}
            textColor={COLORS.WhiteText}
          />
        </Box>*/}
      </Box>
    );
  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box mt={2}>
        <PageHeader
          title="Client Details"
          subtitle="Profile, bookings and invoices for this client."
        />
        <Box sx={{
            bgcolor: COLORS.WhiteBackground,
            borderRadius: 2,
            boxShadow: 3,
            position: "relative",
            minHeight: "80vh",
        }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
              <CircularProgress />
            </Box>
          )}
          
          {error && !loading && (
            <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
          )}

          {!loading && !error && (
            <>
              <Tabs value={tab} aria-label="client details tabs" sx={{ borderBottom: 1, borderColor: 'divider' }}
              onChange={handleTabChange}>
                  <Tab label="Personal Information" sx={{
                    textTransform: "none",
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    color: (t) => t.palette.text.secondary,
                    "&:hover": { bgcolor: "transparent" },
                  }}/>
                 
                  
                  <Tab label="Booking History" sx={{
                    textTransform: "none",
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    color: (t) => t.palette.text.secondary,
                    "&:hover": { bgcolor: "transparent" },
                  }}/>

                  <Tab label="Invoices History" sx={{
                    textTransform: "none",
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    color: (t) => t.palette.text.secondary,
                    "&:hover": { bgcolor: "transparent" },
                  }}/>
              </Tabs>
              <Box p={2} display="flex" gap={3}>
                <Box flex={2} ref={tabPanelRef}>
                  { tab === 1 ?(
                  <>
                  {/** appts history view */}
                  <Box display="flex" flexDirection="column" gap={1.5} >

       <Box
  mb={1}
  display="flex"
  gap={1}
  flexWrap="wrap"
  alignItems="flex-end"
>
  <Box minWidth={90}>
    <CustomLabel text="From" variant="body1" gutterBottom />
    <CustomDateField
    sx={{width:'120px'}}
      value={from || ""}
      onChange={(value) => {
        setFrom(value || null);
        setPage(1);
      }}
      fullWidth
    />
  </Box>

  <Box minWidth={90}>
    <CustomLabel text="To" variant="body1" gutterBottom />
    <CustomDateField
    sx={{width:'120px'}}
      value={to || ""}
      onChange={(value) => {
        setTo(value || null);
        setPage(1);
      }}
      fullWidth
    />
  </Box>

  <Box display="flex" alignItems="flex-end" gap={1} mb={0.5}>
    <Typography sx={{ fontSize: '0.875rem', color: '#9ca3af', mb: '6px' }}>
      Rows per page:
    </Typography>
    <Select
      value={size}
      onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}
      sx={{ height: '32px', fontSize: '0.875rem', width: '80px' }}
    >
      <MenuItem value={10}>10</MenuItem>
      <MenuItem value={20}>20</MenuItem>
      <MenuItem value={50}>50</MenuItem>
      <MenuItem value={100}>100</MenuItem>
    </Select>
  </Box>

  <Box display={'flex'} alignItems={'flex-end'} gap={0.5} mb={0.5}>
      <IconButton
      disabled={page <= 1}
      onClick={() => setPage((p) => Math.max(p - 1, 1))}
      sx={{ color: page > 1 ? '#9ca3af' : '#ccc', p: 0.5 }}
    >
      <ArrowBackIosNew fontSize="small" />
    </IconButton>

    <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: '#9ca3af', alignSelf: 'center' }}>
      {page} / {Math.max(1, Math.ceil(total / size))}
    </Typography>

    <IconButton
      disabled={page >= Math.ceil(total / size)}
      onClick={() => setPage((p) => Math.min(p + 1, Math.ceil(total / size)))}
      sx={{ color: page < Math.ceil(total / size) ? '#9ca3af' : '#ccc', p: 0.5 }}
    >
      <ArrowForwardIos fontSize="small" />
    </IconButton>
  </Box>
  <Box display="flex" alignItems="center" gap={1} mb={1}>
    <Typography sx={{ fontSize: '0.875rem', color: '#9ca3af' }}>
      Total Appointments: {total}
    </Typography>
  </Box>

</Box>

      {appointmentsLoading ? (
        <Box display="flex" justifyContent="center" py={2}><CircularProgress /></Box>
      ) : appointments.length === 0 ? (
        <CustomLabel text="No bookings found." />
      ) : appointments.map((appt) => {
        const start = appt.start_at ? new Date(appt.start_at) : null;
        const end = appt.end_at ? new Date(appt.end_at) : null;
        const dateLabel = start
          ? start.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
          : "—";
        const timeLabel =
          start && end
            ? `${start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} – ${end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
            : "—";
        const promoCode = appt?.promo?.code ? String(appt.promo.code) : "";
        const invoiceId = appt?.invoice_id ? String(appt.invoice_id) : "";
        const serviceLabel = appt.service?.name || appt.notes || "Appointment";
        const serviceWithTime = `${serviceLabel} - ${timeLabel}`;
        const totalPriceValue = appt?.total_price;
        const hasTotalPrice = totalPriceValue !== undefined && totalPriceValue !== null && totalPriceValue !== "";
        const totalPriceText = hasTotalPrice
          ? `Total Price: ${Number(totalPriceValue)}`
          : "";
        const status = (appt.status || "").toUpperCase();
        const statusColor =
          status === "DONE" ? "#22c55e" :
          status === "PENDING" ? COLORS.MainColor :
          status === "IN_PROGRESS" ? "#f59e0b" :
          status === "CANCELLED" ? "#ef4444" :
          status === "CONFIRMED" ? "#22c55e" :
          "#9ca3af";

        const rawInvoiceStatus = String(appt?.invoice_status || "").toUpperCase();
        const invoiceStatus = rawInvoiceStatus === "VOID" ? "CANCELLED" : (rawInvoiceStatus || "—");
        const invoiceStatusColor =
          invoiceStatus === "PAID" ? "#16a34a" :
          invoiceStatus === "UNPAID" ? "#ef4444" :
          invoiceStatus === "PARTIALLY_PAID" ? "#f59e0b" :
          invoiceStatus === "PARTIALLY_REFUNDED" ? "#f97316" :
          invoiceStatus === "REFUNDED" ? "#0284c7" :
          invoiceStatus === "CANCELLED" ? "#6b7280" :
          "#9ca3af";

        return (
          <Box
            key={appt.id}
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box>
              <Box display="flex" alignItems="center" gap={2}>
                <CustomLabel text={dateLabel} fontWeight={700} fontSize={16} />
                {invoiceId ? (
                  <CustomLabel text={`invoice: ${invoiceId}`} fontWeight={600} fontSize={13} />
                ) : null}
              </Box>
              <CustomLabel
                text={serviceWithTime}
                fontSize={14}
                color="#4b5563"
              />
              <CustomLabel
                text={
                  appt.unit?.name
                    ? `Branch: ${appt.unit.name}`
                    : ""
                }
                fontSize={12}
                color="#6b7280"
              />
              <CustomLabel
                text={
                  totalPriceText
                    ? promoCode
                      ? `${totalPriceText} - Promo: ${promoCode}`
                      : totalPriceText
                    : ""
                }
                fontSize={12}
                color="#6b7280"
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 110 }}>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#fff",
                  backgroundColor: statusColor,
                  textAlign: "center",
                }}
              >
                {status || "—"}
              </Box>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#fff",
                  backgroundColor: invoiceStatusColor,
                  textAlign: "center",
                }}
              >
                {invoiceStatus}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>


                  </>
                  ) : tab === 2 ? (
                    <Box display="flex" flexDirection="column" gap={1.5}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
                        <Typography sx={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                          Total Invoices: {invoiceTotal}
                        </Typography>
                        <Box display={'flex'} alignItems={'center'} gap={0.5}>
                          <IconButton
                            disabled={invoicePage <= 1}
                            onClick={() => setInvoicePage((p) => Math.max(p - 1, 1))}
                            sx={{ color: invoicePage > 1 ? '#9ca3af' : '#ccc', p: 0.5 }}
                          >
                            <ArrowBackIosNew fontSize="small" />
                          </IconButton>
                          <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: '#9ca3af' }}>
                            {invoicePage} / {invoiceTotalPages}
                          </Typography>
                          <IconButton
                            disabled={invoicePage >= invoiceTotalPages}
                            onClick={() =>
                              setInvoicePage((p) => Math.min(p + 1, invoiceTotalPages))
                            }
                            sx={{ color: invoicePage < invoiceTotalPages ? '#9ca3af' : '#ccc', p: 0.5 }}
                          >
                            <ArrowForwardIos fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                          gap: 1.5,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: "10px",
                            backgroundColor: "#f5f5f5",
                            border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                          }}
                        >
                          <CustomLabel text="Total" fontWeight="600" fontSize="0.9rem" />
                          <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                            {invoiceTotals.total}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: "10px",
                            backgroundColor: "#f5f5f5",
                            border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                          }}
                        >
                          <CustomLabel text="Paid" fontWeight="600" fontSize="0.9rem" />
                          <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                            {invoiceTotals.paid}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: "10px",
                            backgroundColor: "#f5f5f5",
                            border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                          }}
                        >
                          <CustomLabel text="Remaining" fontWeight="600" fontSize="0.9rem" />
                          <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                            {invoiceTotals.remaining}
                          </Box>
                        </Box>
                      </Box>

                      {invoicesLoading ? (
                        <Box display="flex" justifyContent="center" py={2}><CircularProgress /></Box>
                      ) : invoicesError ? (
                        <CustomLabel text={invoicesError} color={COLORS.RED} />
                      ) : invoices.length === 0 ? (
                        <CustomLabel text="No invoices found." />
                      ) : (
                        pagedInvoices.map((inv: any, idx: number) => {
                          const invoiceNumber =
                            inv?.Invoice_number ||
                            inv?.invoice_number ||
                            inv?.invoiceNumber ||
                            inv?.id ||
                            "-";
                          const total = Number(inv?.total ?? inv?.total_amount ?? inv?.net_amount) || 0;
                          const balance = Number(inv?.balance ?? inv?.remaining ?? 0) || 0;
                          const paid = total - balance;
                          const status = String(inv?.status || "").toUpperCase() || "-";
                          const dateRaw = inv?.issuedAt || inv?.createdAt || inv?.created_at || inv?.date;
                          const dateLabel = dateRaw
                            ? new Date(dateRaw).toLocaleDateString(undefined, {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })
                            : "-";

                          return (
                            <Box
                              key={inv?.id || inv?.invoice_id || invoiceNumber || idx}
                              sx={{
                                border: "1px solid #e5e7eb",
                                borderRadius: 2,
                                p: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <CustomLabel text={`Invoice #: ${invoiceNumber}`} fontWeight={700} fontSize={15} />
                                <CustomLabel text={`Date: ${dateLabel}`} fontSize={12} color="#6b7280" />
                                <CustomLabel text={`Total: ${total.toFixed(2)} | Paid: ${paid.toFixed(2)} | Remaining: ${balance.toFixed(2)}`} fontSize={12} color="#6b7280" />
                              </Box>
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 10,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#fff",
                                  backgroundColor: COLORS.MainColor,
                                  minWidth: 100,
                                  textAlign: "center",
                                }}
                              >
                                {status}
                              </Box>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  ):(<> 
                      <CustomLabel text="Client Name" variant="subtitle1" gutterBottom />
                      <CustomTextField
                        fullWidth
                        value={name}
                        disabled={!isEditing}
                        onChange={(e) => setName(e.target.value)}
                      />

                      <CustomLabel mt={2} text="Client Email" variant="subtitle1" gutterBottom />
                      <CustomTextField
                        fullWidth
                        value={email}
                        disabled={!isEditing}
                        onChange={(e) => setEmail(e.target.value)}
                      />

                      <Box mt={2} display="flex" gap={2} alignItems="flex-end">
                        <Box minWidth={0} flex={0.2}>
                          <CustomLabel text="Country" variant="subtitle1" gutterBottom />
                          <CustomSelect
                            label="Country"
                            value={country}
                            options={countries.map((c) => ({
                              label: `+${c.phone} - ${c.label}`,
                              value: `+${c.phone}`,
                            }))}
                            disabled={!isEditing}
                            onChange={(v: any) => setCountry(String(v))}
                            fullWidth
                            searchable
                          />
                        </Box>
                        <Box flex={0.8}>
                          <CustomLabel text="Phone Number" variant="subtitle1" gutterBottom />
                          <CustomTextField
                            fullWidth
                            value={phone}
                            disabled={!isEditing}
                            onChange={(e) => setPhone(e.target.value)}
                          />
                        </Box>
                      </Box>

                      <Box mt={2} display="grid" gridTemplateColumns="1fr 1fr" gap={2} alignItems="flex-end">
                        <Box minWidth={0}>
                          <CustomLabel text="Gender" variant="subtitle1" gutterBottom />
                          <CustomTextField
                            fullWidth
                            minWidth="0px"
                            value={gender}
                            disabled={!isEditing}
                            onChange={(e) => setGender(e.target.value)}
                          />
                        </Box>
                        <Box minWidth={0} display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap={1}>
                          <Box minWidth={0}>
                            <CustomLabel text="Day" variant="subtitle1" gutterBottom />
                            <CustomTextField
                            fullWidth
                            minWidth="0px"
                            value={dobDay}
                            disabled={!isEditing}
                            select
                            onChange={(e) => updateDobFromParts(e.target.value as string, dobMonth, dobYear)}
                          >
                              <MenuItem value="" disabled>
                                Day
                              </MenuItem>
                              {days.map((d) => (
                                <MenuItem key={d} value={d}>{d}</MenuItem>
                              ))}
                            </CustomTextField>
                          </Box>
                          <Box minWidth={0}>
                            <CustomLabel text="Month" variant="subtitle1" gutterBottom />
                            <CustomTextField
                            fullWidth
                            minWidth="0px"
                            value={dobMonth}
                            disabled={!isEditing}
                            select
                            onChange={(e) => updateDobFromParts(dobDay, e.target.value as string, dobYear)}
                          >
                              <MenuItem value="" disabled>
                                Month
                              </MenuItem>
                              {months.map((m) => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                              ))}
                            </CustomTextField>
                          </Box>
                          <Box minWidth={0}>
                            <CustomLabel text="Year" variant="subtitle1" gutterBottom />
                            <CustomTextField
                            fullWidth
                            minWidth="0px"
                            value={dobYear}
                            disabled={!isEditing}
                            select
                            onChange={(e) => updateDobFromParts(dobDay, dobMonth, e.target.value as string)}
                          >
                              <MenuItem value="" disabled>
                                Year
                              </MenuItem>
                              {years.map((y) => (
                                <MenuItem key={y} value={y}>{y}</MenuItem>
                              ))}
                            </CustomTextField>
                          </Box>
                        </Box>
                      </Box>

                      <CustomLabel mt={2} text="Status" variant="subtitle1" gutterBottom />
                      <CustomTextField
                        fullWidth
                        value={status}
                        disabled={!isEditing}
                        select
                        onChange={(e) => setStatus(e.target.value as string)}
                      >
                        <MenuItem value="Verified">Verified</MenuItem>
                        <MenuItem value="Unverified">Unverified</MenuItem>
                      </CustomTextField>

                      <Box mt={10} display="flex" gap={2} alignItems="center" justifyContent="center">
                        <CustomButton
                          label="Edit"
                          textColor={isEditing ? COLORS.WhiteText : COLORS.WhiteText}
                          bgColor={isEditing ? COLORS.BorderColor : COLORS.MainColor}
                          onClick={() => setIsEditing(true)}
                          disabled={isEditing || saving}
                        />
                        <CustomButton
                          label='Save'
                          textColor={COLORS.WhiteText}
                          bgColor={isEditing ? COLORS.MainColor : COLORS.BorderColor}
                          onClick={handleSave}
                          disabled={
                            !isEditing ||
                            saving ||
                            !name.trim() ||
                            !country.trim() ||
                            !phone.trim()
                          }
                          loading={saving}
                        />
                      </Box>
                  </>
                  )}
                </Box>
                {sidePanel}
              </Box>
            </>
          )}
            </Box>
        
    </Box>
    </Box>
  );
};

export default ClientDetails;
